"use client"

import { SimilarProductSiteType } from "@/app/types/product-site";
import { Box, colors, Typography, useTheme } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import TextOverflow from "@v2/components/styled/TextOverflow";
import { numberFormat } from "@v2/helpers/optimizedHelpers";
import { buildItem, trackSelectItem, trackViewItemList, useListImpression } from "@v2/lib/analytics";
import Link from "next/link";
import { Layers } from "lucide-react";
import { useCallback } from "react";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper/types";

const LIST_ID = "pdp_related"
const LIST_NAME = "PDP related products"

const SPACE_BETWEEN = 8
// One source of truth: swiper reads it as its breakpoints, and the stylesheet
// below turns it into the widths the slides have before swiper starts.
const SLIDES_PER_VIEW = { xs: 1.2, xs2: 1.4, sm: 2.4, sm2: 3.5, md: 2, md2: 2.4, lg: 3.4, lg2: 4 } as const
// Below these the active card sits in the middle, full size, with smaller
// neighbours peeking in on both sides, and the list wraps around.
const CENTERED_BREAKPOINTS = ['xs', 'xs2', 'sm'] as const
const SIDE_SCALE = 0.85
// Turning each neighbour so its outer edge falls away makes the row read as
// the front of a large ring rather than a flat strip.
const SIDE_ANGLE = 30
const PERSPECTIVE = 1000

const sideTransform = (distance: number, angle: number) =>
    `perspective(${PERSPECTIVE}px) rotateY(${angle * distance}deg) scale(${1 - distance * (1 - SIDE_SCALE)})`

const slideWidth = (perView: number) => `calc((100% + ${SPACE_BETWEEN}px) / ${perView} - ${SPACE_BETWEEN}px)`

// swiper/css leaves every slide at width:100% until the script runs, so a server
// rendered carousel paints one full width card and then snaps into a row. This is
// the width swiper itself sets inline a moment later — declaring it up front means
// the first paint is already right, and swiper's inline style takes over unnoticed.
const preInitSlideWidths = Object.fromEntries(
    Object.entries(SLIDES_PER_VIEW).map(([breakpoint, perView]) => [
        breakpoint,
        slideWidth(perView),
    ]),
)

// Before the script runs, the first card sits at the start edge. Shifting the
// row by half the leftover space puts it in the middle, where swiper will put it.
const preInitCenterShift = {
    ...Object.fromEntries(CENTERED_BREAKPOINTS.map(breakpoint => [
        breakpoint,
        `translateX(calc((${slideWidth(SLIDES_PER_VIEW[breakpoint])} - 100%) / 2))`,
    ])),
    sm2: 'none',
}

// Turning and scaling from the edge that faces the active card keeps the gap
// between them constant, so the neighbours fold outward instead of drifting away.
// That pivot glides to the middle as a card nears the centre rather than
// switching edges at once: a switch lands while the card is still turned and
// makes it jump when the swipe is released.
function placeOnRing(swiper: SwiperType) {
    const centered = swiper.params.centeredSlides
    // watchSlidesProgress puts the progress on each slide element.
    const slides = swiper.slides as (HTMLElement & { progress: number })[]
    slides.forEach(slide => {
        if (!centered) {
            slide.style.transform = ''
            slide.style.transformOrigin = ''
            return
        }
        const distance = Math.min(Math.abs(slide.progress), 1)
        const isOnLeft = (slide.progress > 0) !== swiper.rtlTranslate
        slide.style.transform = sideTransform(distance, isOnLeft ? -SIDE_ANGLE : SIDE_ANGLE)
        const pivotShift = 50 * Math.min(distance * 4, 1)
        slide.style.transformOrigin = `${50 + (isOnLeft ? pivotShift : -pivotShift)}% 50%`
    })
}

function syncScaleTransition(swiper: SwiperType, duration: number) {
    swiper.slides.forEach(slide => {
        slide.style.transitionDuration = `${duration}ms`
    })
}

// The list is handed down from the page instead of being fetched here, so the
// product links sit in the server markup where a crawler can follow them.
export default function PdpRelatedSiteProducts({ siteProducts }: {
    siteProducts: SimilarProductSiteType[]
}) {
    const theme = useTheme()

    // The carousel is a product list like any other, so it reports impressions
    // and clicks under its own list name.
    const analyticsItem = useCallback((siteProduct: SimilarProductSiteType, index: number) => buildItem({
        variantId: siteProduct.product.product_id,
        name: siteProduct.product.name,
        brand: siteProduct.brand?.en_name,
        colorCode: siteProduct.product.color_code,
        size: siteProduct.product.size,
        tomanPrice: siteProduct.product.price,
        tomanDiscount: siteProduct.product.discount,
        siteProductId: siteProduct.id,
        index,
        listId: LIST_ID,
        listName: LIST_NAME,
    }), [])

    // The carousel sits at the bottom of the page, so most visits never reach
    // it. Reporting on load would count impressions nobody had.
    const visibleProducts = siteProducts.filter(i => i.product)
    const reportImpression = useCallback(
        () => trackViewItemList(visibleProducts.map(analyticsItem), LIST_ID, LIST_NAME),
        [visibleProducts, analyticsItem],
    )
    const impressionRef = useListImpression(reportImpression, visibleProducts.length > 0)

    if (!visibleProducts.length) return null;

    return (
        <>
            <Box sx={{
                bgcolor: '#fff',
                p: 2,
                mt: '4px',
                borderRadius: 1,
                mb: 0.5
            }}>
                <Box ref={impressionRef} aria-hidden sx={{ height: '1px' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 2 }}>
                    <Layers size={20} color={colors.grey[700]} />
                    <Typography sx={{ fontSize: 18, fontWeight: 500, color: colors.grey[800] }}>محصولات مرتبط</Typography>
                </Box>
                <Box
                    sx={{
                        // py: 0.5,
                        display: "flex",
                        alignItems: "center",
                        userSelect: 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        // Stretch over the card's padding so cards are cut at the
                        // card's edge, not 16px inside it.
                        mx: -2,
                        px: 2,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: -1,
                            bottom: 0,
                            width: '1px',
                            zIndex: 10,
                            // boxShadow: "rgba(0, 0, 0, 0.15) 0px 1px 2px, rgba(0, 0, 0, 0.15) 0px 2px 4px, rgba(0, 0, 0, 0.1) 0px 4px 8px, rgba(0, 0, 0, 0.1) 0px 8px 16px, rgba(0, 0, 0, 0.1) 0px 16px 32px, rgba(0, 0, 0, 0.07) 0px 32px 64px",

                        },
                        '& .swiper-pagination': {
                            bottom: 0,
                            '& .swiper-pagination-bullet': {
                                borderRadius: 3,
                                transition: '180ms !important'
                            },
                            '& .swiper-pagination-bullet-active': {
                                background: colors.grey[800],
                            }
                        },
                        // px: 2,
                        // mx: 2
                        '& .swiper-wrapper': {
                            overflow: "visible",
                            // width: "auto",
                            // mr: 2,
                            // ml: 2,
                            gap: `${SPACE_BETWEEN}px`,
                        },
                        '& .swiper-slide': {
                            width: preInitSlideWidths,
                            transitionProperty: 'transform, transform-origin',
                        },
                        '& .swiper:not(.swiper-initialized) .swiper-wrapper': {
                            transform: preInitCenterShift,
                        },
                        '& .swiper:not(.swiper-initialized) .swiper-slide:not(:first-of-type)': {
                            transform: { xs: sideTransform(1, -SIDE_ANGLE), sm2: 'none' },
                            // The RTL plugin mirrors this: it lands as 'right center',
                            // the edge that faces the first card.
                            transformOrigin: 'left center',
                        },
                        // Once swiper is running it puts the gap on the slides itself,
                        // so the wrapper must stop adding its own.
                        '& .swiper-initialized .swiper-wrapper': {
                            gap: 0,
                        },
                        '& .swiper': {
                            overflow: 'visible',
                            // A visible-overflow flex item grows to its content
                            // without this.
                            minWidth: 0,
                            pb: 2.5,
                            // pr: 2,
                            // pl: 2,
                        }
                    }}
                >
                    <Swiper
                        slidesPerView={SLIDES_PER_VIEW.lg2}
                        spaceBetween={SPACE_BETWEEN}
                        watchSlidesProgress
                        onSetTranslate={placeOnRing}
                        onSetTransition={syncScaleTransition}
                        onBreakpoint={placeOnRing}
                        modules={[Pagination]}
                        pagination={{
                            enabled: true,
                            dynamicBullets: true,
                            dynamicMainBullets: 2,
                        }}
                        breakpoints={Object.fromEntries(
                            Object.entries(SLIDES_PER_VIEW).map(([breakpoint, perView]) => [
                                theme.breakpoints.values[breakpoint as keyof typeof SLIDES_PER_VIEW],
                                {
                                    slidesPerView: perView,
                                    centeredSlides: CENTERED_BREAKPOINTS.includes(breakpoint as typeof CENTERED_BREAKPOINTS[number]),
                                    loop: CENTERED_BREAKPOINTS.includes(breakpoint as typeof CENTERED_BREAKPOINTS[number]),
                                },
                            ]),
                        )}
                    >
                        {siteProducts.filter(i => i.product).map((siteProduct, index) => {
                            const isFirst = index === 0;
                            const isLast = index === siteProducts.length - 1
                            const splitTitle = siteProduct.title.split('-')
                            const firstTitle = splitTitle.shift()?.trim();
                            const secondTitle = splitTitle.join().trim();

                            const defaultImage = siteProduct.product.images?.find(i => i.default) ?? siteProduct.product.images?.[0]
                            return (
                                <SwiperSlide key={siteProduct.id}>
                                    <Box
                                        component={Link}
                                        // On the click, not the pointer down: this is a
                                        // swiper, and every swipe of the carousel starts
                                        // with a pointer down on a slide nobody chose.
                                        onClick={() => trackSelectItem(analyticsItem(siteProduct, index), LIST_ID, LIST_NAME)}
                                        // No pid: this carousel is now in the server markup, so every
                                        // href here is a url a crawler will queue. A pid points at the
                                        // same page as its canonical, so carrying one would only spend
                                        // crawl budget on a duplicate of a page we already want indexed.
                                        href={'/fa/products/' + siteProduct.slug}
                                        sx={{
                                            display: 'block',
                                            background: "#fff",
                                            borderTopLeftRadius: isFirst ? 8 : 0,
                                            borderBottomLeftRadius: isFirst ? 8 : 0,
                                            borderTopRightRadius: isLast ? 8 : 0,
                                            borderBottomRightRadius: isLast ? 8 : 0,
                                            overflow: "hidden",
                                            transition: '350ms',
                                            border: '2px solid',
                                            borderColor: '#ededed',
                                            borderRadius: 1,
                                            '&:hover': {
                                                bgcolor: colors.grey[50],
                                                borderColor: colors.grey[200],
                                            }
                                        }}
                                    >

                                        <Box sx={{
                                            background: 'radial-gradient(circle at 50% 40%, #f9f9f8 0%, #efefed 100%)',
                                            width: '100%',
                                            aspectRatio: '1 / 1',
                                            position: 'relative',
                                        }}>
                                            <MediaV2
                                                media_id={defaultImage?.id}
                                                alt={defaultImage?.alt ?? siteProduct.title}
                                                withLoading
                                                boxProps={{ sx: { position: 'absolute', inset: 0 } }}
                                                imageProps={{
                                                    fill: true,
                                                    style: {
                                                        objectFit: 'contain',
                                                        mixBlendMode: 'multiply',
                                                    }
                                                }}
                                                width={600}
                                            />
                                        </Box>


                                        <Box sx={{ p: 2, py: 1 }}>
                                            <TextOverflow sx={{
                                                fontSize: 13,
                                                fontWeight: 400,
                                                color: colors.grey[800]
                                            }}>
                                                {firstTitle}
                                            </TextOverflow>
                                            <TextOverflow className="en-num" sx={{
                                                direction: 'rtl',
                                                textAlign: 'right',
                                                color: colors.grey[600],
                                                fontSize: 12,
                                                mt: 1
                                            }}>
                                                {secondTitle}
                                            </TextOverflow>
                                            <Box sx={{ display: "flex", alignItems: "center", mt: 1, height: 27, }}>
                                                {siteProduct.product.need_inquiry ? (
                                                    <Typography sx={{
                                                        fontSize: 13,
                                                        fontWeight: 400,
                                                        color: colors.blue[900],
                                                    }}>
                                                        نیازمند استعلام
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                fontWeight: 700,
                                                                color: "#333",
                                                                mr: 0.5,
                                                            }}
                                                        >
                                                            {(siteProduct.product.final_price !== 0 && siteProduct.product.quantity > 0) && (
                                                                numberFormat(siteProduct.product.final_price)
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 11,
                                                                fontWeight: 400,
                                                                color: colors.grey[600],
                                                            }}
                                                        >
                                                            {siteProduct.product.quantity > 0 && siteProduct.product.price !== 0 ? "تومانء" : "نـامــوجـود"}

                                                        </Typography></>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </SwiperSlide>
                            )
                        })}
                        {siteProducts.length === 2 && (
                            <>
                                <SwiperSlide key={100}></SwiperSlide>
                            </>
                        )}
                        {siteProducts.length === 1 && (
                            <>
                                <SwiperSlide key={99}></SwiperSlide>
                                <SwiperSlide key={100}></SwiperSlide>
                            </>
                        )}
                    </Swiper>
                </Box>
            </Box>
        </>
    )
}
